import { useState } from 'react'
import { Link, createFileRoute, redirect, useBlocker, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Pencil } from 'lucide-react'
import {
  getActivationsAdminStudentProjectsListQueryKey,
  getActivationsAdminStudentProjectsPreviewQueryKey,
  getActivationsAdminStudentProjectsRetrieveQueryKey,
  getV1MeRetrieveQueryOptions,
  useActivationsAdminStudentProjectsArchive,
  useActivationsAdminStudentProjectsDestroy,
  useActivationsAdminStudentProjectsPartialUpdate,
  useActivationsAdminStudentProjectsPublish,
  useActivationsAdminStudentProjectsPreview,
  useActivationsAdminStudentProjectsRetrieve,
  useActivationsAdminStudentProjectsUpdate,
} from '@/api/client'
import type { AdminStudentProjectWriteRequest } from '@/api/model'
import { PageHeader } from '@/components/page-header'
import { Button } from '@/components/ui/button'
import { ActivationForm } from '@/components/activation/activation-form'
import type {
  ActivationPublishedPatch,
  ActivationWriteRequest,
} from '@/components/activation/activation-form-schema'
import { fromContainer } from '@/components/activation/activation-form-schema'
import { ActivationStateBadge } from '@/components/activation/activation-state-badge'
import { ActivationLifecycleActions } from '@/components/activation/activation-lifecycle-actions'
import { ActivationDetailBody } from '@/components/activation/activation-detail-body'
import { ProjectRemindersSection } from '@/components/activation/project-reminders-section'
import { ProjectSubmissionsSection } from '@/components/activation/project-submissions-section'
import { StudentProjectPublicationPreview } from '@/components/activation/student-project-publication-preview'
import { publicationDisabledReason } from '@/components/activation/student-project-publication-guard'

export const Route = createFileRoute('/_panel/zadania/projekty/$uid')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(getV1MeRetrieveQueryOptions())
    if (me.status === 200 && me.data.role !== 'admin' && me.data.role !== 'superadmin') {
      throw redirect({ to: '/' })
    }
  },
  component: StudentProjectDetailPage,
})

function StudentProjectDetailPage() {
  const { uid } = Route.useParams()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const query = useActivationsAdminStudentProjectsRetrieve(uid)
  const template = query.data?.status === 200 ? query.data.data : undefined
  const state = template?.state

  const [editing, setEditing] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [notifyStudents, setNotifyStudents] = useState(true)
  useBlocker({
    shouldBlockFn: () => editing && dirty && !window.confirm('Odrzucić niezapisane zmiany szkicu?'),
    enableBeforeUnload: () => editing && dirty,
  })

  const publishMutation = useActivationsAdminStudentProjectsPublish()
  const previewQuery = useActivationsAdminStudentProjectsPreview(uid, {
    query: { enabled: state === 'draft' && !editing },
  })
  const preview = previewQuery.data?.status === 200 ? previewQuery.data.data : undefined
  const archiveMutation = useActivationsAdminStudentProjectsArchive()
  const destroyMutation = useActivationsAdminStudentProjectsDestroy()
  const updateMutation = useActivationsAdminStudentProjectsUpdate()
  const patchMutation = useActivationsAdminStudentProjectsPartialUpdate()

  const refresh = async () => {
    await queryClient.invalidateQueries({
      queryKey: getActivationsAdminStudentProjectsRetrieveQueryKey(uid),
    })
    await queryClient.invalidateQueries({
      queryKey: getActivationsAdminStudentProjectsListQueryKey(),
    })
    await queryClient.invalidateQueries({
      queryKey: getActivationsAdminStudentProjectsPreviewQueryKey(uid),
    })
  }

  const handleDraftSave = async (payload: ActivationWriteRequest) => {
    const response = await updateMutation.mutateAsync({
      uid,
      data: payload as AdminStudentProjectWriteRequest,
    })
    if (response.status < 400) {
      await refresh()
      setDirty(false)
      setEditing(false)
    }
    return response
  }

  const handlePublishedSave = async (payload: ActivationPublishedPatch) => {
    const response = await patchMutation.mutateAsync({ uid, data: payload })
    if (response.status < 400) {
      await refresh()
      setDirty(false)
      setEditing(false)
    }
    return response
  }

  if (query.isLoading) {
    return (
      <>
        <PageHeader title="Projekt uczniowski" />
        <p className="p-6 text-sm text-muted-foreground">Ładowanie…</p>
      </>
    )
  }

  if (!template) {
    return (
      <>
        <PageHeader title="Projekt uczniowski" />
        <div className="flex flex-col items-start gap-3 p-6">
          <p className="text-sm text-muted-foreground">Nie znaleziono projektu uczniowskiego.</p>
          <Button asChild variant="outline" size="sm">
            <Link to="/zadania/projekty">
              <ArrowLeft /> Wróć do listy
            </Link>
          </Button>
        </div>
      </>
    )
  }

  const isDraft = state === 'draft'
  const isPublished = state === 'published'
  const canEdit = isDraft || isPublished

  return (
    <>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <Button asChild variant="ghost" size="icon-sm">
              <Link to="/zadania/projekty">
                <ArrowLeft />
              </Link>
            </Button>
            <span className="max-w-xl truncate">{template.name}</span>
            <ActivationStateBadge state={state} />
          </span>
        }
        actions={
          <span className="flex gap-2">
            {canEdit && !editing && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setDirty(false)
                  setEditing(true)
                }}
              >
                <Pencil /> Edytuj
              </Button>
            )}
            <ActivationLifecycleActions
              state={state}
              onPublish={() =>
                publishMutation.mutateAsync({ uid, data: { notify_students: notifyStudents } })
              }
              onArchive={() => archiveMutation.mutateAsync({ uid })}
              onDelete={() => destroyMutation.mutateAsync({ uid })}
              refresh={refresh}
              onDeleted={() => navigate({ to: '/zadania/projekty' })}
              publishDisabledReason={publicationDisabledReason({
                isLoading: previewQuery.isLoading,
                isFetching: previewQuery.isFetching,
                ready: preview?.ready,
              })}
              publishConfirmDescription={
                notifyStudents
                  ? 'Projekt stanie się dostępny w aplikacji. Zostanie też utworzona osobna próba wysłania powiadomień push.'
                  : 'Projekt stanie się dostępny w aplikacji bez tworzenia powiadomień push.'
              }
            />
          </span>
        }
      />

      <div className="flex flex-col gap-6 p-6">
        {editing && canEdit ? (
          isPublished ? (
            <ActivationForm
              mode="published-edit"
              initial={fromContainer(template)}
              submitLabel="Zapisz zmiany"
              onCancel={() => {
                setDirty(false)
                setEditing(false)
              }}
              onSubmit={handlePublishedSave}
              onDirtyChange={setDirty}
            />
          ) : (
            <ActivationForm
              mode="draft-edit"
              initial={fromContainer(template)}
              submitLabel="Zapisz zmiany"
              onCancel={() => {
                setDirty(false)
                setEditing(false)
              }}
              onSubmit={handleDraftSave}
              onDirtyChange={setDirty}
            />
          )
        ) : (
          <>
            {isDraft && (
              <StudentProjectPublicationPreview
                preview={preview}
                failed={
                  previewQuery.isError ||
                  (previewQuery.data !== undefined && previewQuery.data.status !== 200)
                }
                notifyStudents={notifyStudents}
                onNotifyStudentsChange={setNotifyStudents}
              />
            )}
            <ActivationDetailBody container={template} />
          </>
        )}

        {(isPublished || state === 'archived') && (
          <ProjectRemindersSection uid={uid} canSend={isPublished} />
        )}

        {(isPublished || state === 'archived') && <ProjectSubmissionsSection uid={uid} />}
      </div>
    </>
  )
}
